import { createTheme, Theme } from '@mui/material/styles'
import { DesignTheme, TextStyle } from '../tokens'

/** Apply DesignTheme colors as CSS variables on :root / documentElement */
export function applyCssVariables(theme: DesignTheme, el: HTMLElement = document.documentElement) {
  const { color, radius, space, type, shadow, motion, gradient, blur } = theme
  const set = (k: string, v?: string) => {
    if (v != null) el.style.setProperty(k, v)
  }

  set('--ds-canvas', color.canvas)
  set('--ds-surface', color.surface)
  set('--ds-ink', color.ink)
  set('--ds-ink-muted', color.inkMuted)
  set('--ds-ink-subtle', color.inkSubtle)
  set('--ds-accent', color.accent)
  set('--ds-accent-ink', color.accentInk)
  set('--ds-border', color.border)
  set('--ds-border-strong', color.borderStrong)
  set('--ds-hover', color.hover)
  set('--ds-inverse', color.inverse)
  set('--ds-inverse-ink', color.inverseInk)
  set('--ds-danger', color.danger)
  set('--ds-success', color.success)
  set('--ds-warning', color.warning)
  set('--ds-photo-overlay', color.photoOverlay)

  if (color.alert) {
    set('--ds-alert-ok', color.alert.ok)
    set('--ds-alert-ok-soft', color.alert.okSoft)
    set('--ds-alert-ok-line', color.alert.okLine)
    set('--ds-alert-ok-ink', color.alert.okInk)
    set('--ds-alert-err', color.alert.err)
    set('--ds-alert-err-soft', color.alert.errSoft)
    set('--ds-alert-err-line', color.alert.errLine)
    set('--ds-alert-err-ink', color.alert.errInk)
    set('--ds-alert-warn', color.alert.warn)
    set('--ds-alert-warn-soft', color.alert.warnSoft)
    set('--ds-alert-warn-line', color.alert.warnLine)
    set('--ds-alert-warn-ink', color.alert.warnInk)
    set('--ds-alert-info', color.alert.info)
    set('--ds-alert-info-soft', color.alert.infoSoft)
    set('--ds-alert-info-line', color.alert.infoLine)
    set('--ds-alert-info-ink', color.alert.infoInk)
  }
  set('--ds-immersive', color.immersive)
  set('--ds-immersive-ink', color.immersiveInk)

  set('--ds-night-canvas', color.night.canvas)
  set('--ds-night-surface', color.night.surface)
  set('--ds-night-ink', color.night.ink)
  set('--ds-night-ink-muted', color.night.inkMuted)
  set('--ds-night-ink-subtle', color.night.inkSubtle)
  set('--ds-night-border', color.night.border)

  if (gradient) {
    set('--ds-gradient-spectrum', gradient.spectrum)
    set('--ds-gradient-spectrum-soft', gradient.spectrumSoft)
    set('--ds-accent-deep-a', gradient.accentDeepA)
    set('--ds-accent-deep-b', gradient.accentDeepB)
  }

  // Individual accent-dot swatches (same values used for GalleryLibrary's
  // ColorDot filter row) — exposed as CSS vars so raw-CSS/night surfaces
  // can reference a single brand hue instead of hardcoding hex.
  theme.accentDots?.forEach((c, i) => set(`--ds-accent-dot-${i + 1}`, c))

  set('--ds-radius-sm', `${radius.sm}px`)
  set('--ds-radius-md', `${radius.md}px`)
  set('--ds-radius-lg', `${radius.lg}px`)
  set('--ds-radius-xl', `${radius.xl}px`)
  set('--ds-radius-pill', `${radius.pill}px`)

  Object.entries(space).forEach(([k, v]) => set(`--ds-space-${k}`, `${v}px`))

  set('--ds-font', type.fontFamily)
  set('--ds-font-mono', type.fontFamilyMono)
  set('--ds-shadow-soft', shadow.soft)
  set('--ds-shadow-lift', shadow.lift)
  set('--ds-shadow-deep', shadow.deep)
  set('--ds-shadow-night', shadow.night)
  set('--ds-ease', motion.ease)
  set('--ds-ease-out', motion.easeOut)
  set('--ds-duration-fast', motion.fast)
  set('--ds-duration-normal', motion.normal)
  set('--ds-duration-slow', motion.slow)

  if (blur) {
    set('--ds-blur-sm', blur.sm)
    set('--ds-blur-md', blur.md)
    set('--ds-blur-lg', blur.lg)
  }

  if (type.textStyles) {
    Object.entries(type.textStyles).forEach(([name, style]) => {
      const s = style as TextStyle
      set(`--ds-text-${name}-size`, s.fontSize)
      set(`--ds-text-${name}-weight`, String(s.fontWeight))
      set(`--ds-text-${name}-tracking`, s.letterSpacing)
      set(`--ds-text-${name}-leading`, String(s.lineHeight))
    })
  }

  el.setAttribute('data-theme', theme.mode)
}

/** `TextStyle` → MUI `CSSProperties`-shaped typography variant */
function textStyleToCss(s: TextStyle, mono?: string) {
  return {
    fontSize: s.fontSize,
    fontWeight: s.fontWeight,
    letterSpacing: s.letterSpacing,
    lineHeight: s.lineHeight,
    fontFamily: s.mono && mono ? mono : undefined,
  }
}

/**
 * MUI theme for the album shell / new product surfaces.
 *
 * This is also the theme registered at the app root (see `App.tsx`), so it
 * is written as a strict superset of the legacy Canva-era `editorTheme`
 * contract declared in `src/theme/index.ts` (`palette.surface`,
 * `palette.interactive`, `palette.brand`, `palette.customText`, and the
 * `headingLarge/headingMedium/headingSmall/description/large` typography
 * variants). Every one of those keys is derived from `DesignTheme` tokens
 * below so legacy pages keep rendering — swapping theme packs re-skins
 * them instead of crashing them.
 */
export function createShellTheme(ds: DesignTheme): Theme {
  const ts = ds.type.textStyles

  return createTheme({
    palette: {
      mode: ds.mode,
      primary: {
        main: ds.color.accent,
        contrastText: ds.color.accentInk,
      },
      secondary: {
        main: ds.color.inkMuted,
      },
      background: {
        default: ds.color.canvas,
        paper: ds.color.surface,
      },
      text: {
        primary: ds.color.ink,
        secondary: ds.color.inkMuted,
        disabled: ds.color.inkSubtle,
      },
      error: { main: ds.color.danger },
      success: { main: ds.color.success },
      warning: { main: ds.color.warning },
      info: { main: ds.color.alert.info },
      divider: ds.color.border,
      // --- legacy `editorTheme` contract (src/theme/index.ts) ---
      surface: {
        1: ds.color.surface,
        2: ds.color.hover,
        3: ds.color.border,
        border: ds.color.border,
        hover: ds.color.hover,
        background: ds.color.canvas,
        paper: ds.color.surface,
      },
      interactive: {
        hover: ds.color.hover,
        pressed: ds.color.borderStrong,
        focus: ds.gradient?.accentDeepB ?? ds.color.accent,
      },
      brand: {
        main: ds.color.accent,
        light: ds.color.hover,
        dark: ds.color.ink,
        contrastText: ds.color.accentInk,
      },
      customText: {
        main: ds.color.ink,
        light: ds.color.inkMuted,
        dark: ds.color.ink,
        contrastText: ds.color.canvas,
      },
    },
    typography: {
      fontFamily: ds.type.fontFamily,
      h1: ts
        ? textStyleToCss(ts.d1)
        : {
            fontSize: ds.type.sizeDisplay,
            fontWeight: ds.type.weightBold,
            lineHeight: ds.type.lineTight,
            letterSpacing: '-0.03em',
          },
      h2: ts
        ? textStyleToCss(ts.h2)
        : {
            fontSize: ds.type.sizeXl,
            fontWeight: ds.type.weightBold,
            lineHeight: ds.type.lineTight,
            letterSpacing: '-0.02em',
          },
      h3: ts ? textStyleToCss(ts.h3) : undefined,
      body1: ts
        ? textStyleToCss(ts.body)
        : {
            fontSize: ds.type.sizeMd,
            fontWeight: ds.type.weightRegular,
            lineHeight: ds.type.lineNormal,
          },
      body2: ts
        ? textStyleToCss(ts.small)
        : {
            fontSize: ds.type.sizeSm,
            fontWeight: ds.type.weightRegular,
            lineHeight: ds.type.lineNormal,
          },
      button: {
        textTransform: 'none' as const,
        fontWeight: ds.type.weightSemibold,
      },
      // Satisfy legacy TypographyVariantsOptions required keys from src/theme
      p: { fontSize: ds.type.sizeMd },
      ps: { fontSize: ds.type.sizeSm },
      pt: { fontSize: ds.type.sizeXs },
      label: { fontSize: ds.type.sizeSm, fontWeight: ds.type.weightMedium },
      labelLarge: { fontSize: ds.type.sizeMd, fontWeight: ds.type.weightMedium },
      labelSmall: { fontSize: ds.type.sizeXs, fontWeight: ds.type.weightMedium },
      menuItem: { fontSize: ds.type.sizeSm },
      // Legacy custom variants (src/theme/index.ts) — optional, but real
      // pages read them directly via `variant="headingLarge"` etc.
      headingLarge: ts ? textStyleToCss(ts.d1) : { fontSize: 48, fontWeight: ds.type.weightBold },
      headingMedium: ts ? textStyleToCss(ts.d2) : { fontSize: 28, fontWeight: ds.type.weightBold },
      headingSmall: ts ? textStyleToCss(ts.h3) : { fontSize: 18, fontWeight: ds.type.weightSemibold },
      description: ts
        ? textStyleToCss(ts.bodyLg)
        : { fontSize: ds.type.sizeMd, fontWeight: ds.type.weightRegular },
      large: ts ? textStyleToCss(ts.h3) : { fontSize: 20, fontWeight: ds.type.weightSemibold },
    },
    shape: {
      borderRadius: ds.radius.md,
    },
    spacing: ds.space[1],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: ds.color.canvas,
            color: ds.color.ink,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: ds.radius.pill,
            padding: `${ds.space[2]}px ${ds.space[4]}px`,
            transition: `all ${ds.motion.normal} ${ds.motion.ease}`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: ds.radius.pill,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: ds.radius.sm,
            fontWeight: ds.type.weightMedium,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: ds.radius.lg,
            borderColor: ds.color.border,
            transition: `box-shadow ${ds.motion.normal} ${ds.motion.ease}, transform ${ds.motion.normal} ${ds.motion.ease}`,
            '&:hover': {
              boxShadow: ds.shadow.lift,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: ds.radius.md,
            boxShadow: ds.shadow.deep ?? ds.shadow.lift,
            border: `1px solid ${ds.color.border}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: ds.radius.sm,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: ds.color.borderStrong,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: ds.color.accent,
                borderWidth: 1.5,
              },
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 2,
            borderRadius: 2,
            background: ds.gradient?.spectrum ?? ds.color.accent,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: ds.radius.sm,
            fontSize: ds.type.sizeXs,
            padding: '6px 10px',
            backgroundColor: ds.color.ink,
          },
        },
      },
    },
  })
}

/**
 * Nested theme for pattern / Babylon chrome only.
 * Keeps editor visually stable while shell evolves.
 */
export function createEditorTheme(ds: DesignTheme): Theme {
  return createTheme({
    ...createShellTheme(ds),
    palette: {
      ...createShellTheme(ds).palette,
      background: {
        default: ds.color.surface,
        paper: ds.color.surface,
      },
    },
  })
}
